import { joiResolver } from '@hookform/resolvers/joi';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { stringToPayloadFormat } from 'web-library';
import { GenerateLinkData } from '../../../models';
import schemaForgotPassword from '../schema/schemaForgotPassword';
import {
  GenerateResetRequest,
  GenerateResetResponse,
  usePostForgotPassword,
} from '../../../api-refactored/user/usePostForgotPassword';
import { UseCustomMutationReturn } from '../../../api-refactored/utils/useCustomMutation';
import { GenericErrorResponse } from '../../../api-refactored/typings';

export interface UseForgotPasswordFormReturn {
  /**
   * Function to clear info text
   */
  clearInfoText: () => void;
  /**
   * React hook form object for forgot password form
   */
  form: UseFormReturn<GenerateLinkData>;
  /**
   * Function to generate reset password link
   */
  generateResetLink: (formData: GenerateLinkData) => void;
  /**
   * Info text string
   */
  infoText: string;
  /**
   * React query mutation object
   */
  forgotPasswordMutation: UseCustomMutationReturn<
    GenerateResetResponse,
    AxiosError<GenericErrorResponse>,
    GenerateResetRequest,
    unknown
  >;
  /**
   * show captcha flag
   */
  showCaptcha: boolean;
  /**
   * show successful message flag
   */
  showSuccess: boolean;
}

/**
 * A hook to handle forget password logic
 * @returns
 */
export const useForgotPasswordForm = (): UseForgotPasswordFormReturn => {
  const [infoText, setInfoText] = useState('');
  const { t } = useTranslation();
  const form = useForm<GenerateLinkData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: joiResolver(schemaForgotPassword),
  });
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const forgotPasswordMutation = usePostForgotPassword({
    onSuccess: (data) => {
      setInfoText(`A Password Reset email has been sent to ${data.email}.`);
      setShowCaptcha(false);
      setShowSuccess(true);
    },
    onError: (err) => {
      switch (err.response?.status) {
        case 400:
          if (err.response?.data?.code === '1022' || err.response?.data?.code === '1025') {
            setInfoText(err.response?.data.message!);
          } else {
            setInfoText(t('profile->>ForgotPasswordForm.Invalid user.'));
          }
          break;
        case 404:
          if (err.response?.data?.code === '1024') {
            setInfoText(
              'Your account has been expired.'
             );
          } else {
            // If it's generic HttpCode 404, display 'Invalid Username/Date of birth'
            setInfoText(t('profile->>ForgotPasswordForm.Birthday does not match.'));
          }
          break;
        case 410:
          setInfoText(t('profile->>ForgotPasswordForm.Invalid username.'));
          break;
        case 403:
          if (err.response?.data?.code === '1008') {
            setInfoText(t('profile->>ForgotPasswordForm.User account is blocked.'));
          } else if (err.response?.data?.code === '1009') {
            setInfoText(t('profile->>ForgotPasswordForm.User account is locked.'));
          } else if (err.response?.data?.code === '1206') {
            setInfoText(
              t(
                'profile->>ForgotPasswordForm.User account has not been activated. Please contact the administrator for assistance.'
              )
            );
          } else if (err.response?.data?.code === '1007') {
            setInfoText(t('profile->>ForgotPasswordForm.Birthday does not match.'));
          } else if (err.response?.data?.code === '1003') {
            setInfoText(t('profile->>ForgotPasswordForm.Invalid user.'));
          }
          break;
        default:
          setInfoText('Unknown error. Please try again.');
          break;
      }
      checkFailedAttempt(err);
    },
  });

  const checkFailedAttempt = (err: any) => {
    setShowCaptcha(false);
    setShowSuccess(false);
    if (err.response?.data?.captchaData?.isLocked) {
      setShowCaptcha(true);
    }
  };

  const clearInfoText = () => {
    setInfoText('');
    setShowSuccess(false);
  };

  function generateResetLink(formData: GenerateLinkData) {
    setInfoText('');
    const data: GenerateResetRequest = {
      username: formData.username,
      dateOfBirth: stringToPayloadFormat(formData.dateOfBirth),
    };

    forgotPasswordMutation.mutate(data);
  }

  return {
    generateResetLink,
    clearInfoText,
    form,
    infoText,
    forgotPasswordMutation,
    showCaptcha,
    showSuccess,
  };
};
